/** How the demo XHR guard answers one send(): a local upload of `total` bytes or an inert API answer. */
export type DemoXhrAnswer =
  { kind: 'upload'; total: number } | { kind: 'inert'; status: number; body: string | null };

type Decide = (method: string, url: string, body: unknown) => DemoXhrAnswer | undefined;

interface LocalResponse {
  readyState: number;
  status: number;
  statusText: string;
  headers: string;
  body: string;
  url: string;
}

const STATUS_TEXT: Record<number, string> = { 200: 'OK', 204: 'No Content' };

// Spacing of the local upload's progress events, so the progress bar visibly fills.
const STEP_MS = 150;

/** Bytes a request body carries as a file, or undefined when it carries none. */
export function fileBodySize(body: unknown): number | undefined {
  if (body instanceof Blob) {
    return body.size;
  }
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    return body.byteLength;
  }
  if (body instanceof FormData) {
    let size = 0;
    body.forEach((value) => {
      size += typeof value === 'string' ? value.length : value.size;
    });
    return size;
  }
  return undefined;
}

/** A subclass of `Native` whose send() asks `decide` first; a local answer replays the browser's events and sends nothing. */
export function demoXhrClass(Native: typeof XMLHttpRequest, decide: Decide): typeof XMLHttpRequest {
  return class DemoXhr extends Native {
    #method = 'GET';
    #url = '';
    #local: LocalResponse | undefined;
    #timers: number[] = [];

    override open(
      method: string,
      url: string | URL,
      async = true,
      username?: string | null,
      password?: string | null
    ): void {
      this.#clearTimers();
      this.#local = undefined;
      this.#method = method.toUpperCase();
      this.#url = String(url);
      super.open(method, url, async, username, password);
    }

    override send(body?: Document | XMLHttpRequestBodyInit | null): void {
      const answer = decide(this.#method, this.#url, body);
      if (!answer) {
        super.send(body);
        return;
      }
      this.#local = {
        readyState: Native.OPENED,
        status: 0,
        statusText: '',
        headers: '',
        body: '',
        url: this.#url,
      };
      if (answer.kind === 'upload') {
        this.#playUpload(answer.total);
      } else {
        this.#later(0, () => {
          this.#progress(this, 'loadstart', 0, 0);
          this.#respond(answer.status, answer.body);
        });
      }
    }

    override abort(): void {
      const local = this.#local;
      if (!local) {
        super.abort();
        return;
      }
      this.#clearTimers();
      if (local.readyState !== Native.DONE) {
        this.#setState(Native.DONE, { status: 0, statusText: '' });
        for (const target of [this.upload, this]) {
          this.#progress(target, 'abort', 0, 0);
          this.#progress(target, 'loadend', 0, 0);
        }
      }
      this.#local = { ...local, readyState: Native.UNSENT, status: 0, statusText: '', body: '' };
    }

    override get readyState() {
      return this.#local ? this.#local.readyState : super.readyState;
    }

    override get status() {
      return this.#local ? this.#local.status : super.status;
    }

    override get statusText() {
      return this.#local ? this.#local.statusText : super.statusText;
    }

    override get responseURL() {
      return this.#local ? this.#local.url : super.responseURL;
    }

    override get responseText() {
      return this.#local ? this.#local.body : super.responseText;
    }

    override get response(): unknown {
      const local = this.#local;
      if (!local) {
        return super.response as unknown;
      }
      const { body } = local;
      if (this.responseType === 'json') {
        return body ? (JSON.parse(body) as unknown) : null;
      }
      if (this.responseType === 'blob') {
        return new Blob([body]);
      }
      if (this.responseType === 'arraybuffer') {
        return new TextEncoder().encode(body).buffer;
      }
      // Browsers parse only HTML and XML into a document, so a JSON or empty body reads as null.
      return this.responseType === 'document' ? null : body;
    }

    override getAllResponseHeaders(): string {
      return this.#local ? this.#local.headers : super.getAllResponseHeaders();
    }

    override getResponseHeader(name: string): string | null {
      const local = this.#local;
      if (!local) {
        return super.getResponseHeader(name);
      }
      const line = local.headers
        .split('\r\n')
        .find((header) => header.toLowerCase().startsWith(`${name.toLowerCase()}:`));
      return line ? line.slice(line.indexOf(':') + 1).trim() : null;
    }

    #playUpload(total: number) {
      this.#later(0, () => {
        this.#progress(this, 'loadstart', 0, 0);
        this.#progress(this.upload, 'loadstart', 0, total);
      });
      this.#later(STEP_MS, () => {
        this.#progress(this.upload, 'progress', Math.round(total / 2), total);
      });
      this.#later(2 * STEP_MS, () => {
        for (const type of ['progress', 'load', 'loadend']) {
          this.#progress(this.upload, type, total, total);
        }
        this.#respond(200, null);
      });
    }

    #respond(status: number, body: string | null) {
      const text = body ?? '';
      this.#setState(Native.HEADERS_RECEIVED, {
        status,
        statusText: STATUS_TEXT[status] ?? '',
        headers: body === null ? '' : 'content-type: application/json\r\n',
      });
      this.#setState(Native.LOADING, { body: text });
      this.#progress(this, 'progress', text.length, text.length);
      this.#setState(Native.DONE);
      this.#progress(this, 'load', text.length, text.length);
      this.#progress(this, 'loadend', text.length, text.length);
    }

    #setState(readyState: number, patch: Partial<LocalResponse> = {}) {
      if (!this.#local) {
        return;
      }
      this.#local = { ...this.#local, ...patch, readyState };
      this.dispatchEvent(new Event('readystatechange'));
    }

    #progress(target: EventTarget, type: string, loaded: number, total: number) {
      target.dispatchEvent(new ProgressEvent(type, { lengthComputable: total > 0, loaded, total }));
    }

    #later(ms: number, step: () => void) {
      this.#timers.push(window.setTimeout(step, ms));
    }

    #clearTimers() {
      this.#timers.forEach((timer) => window.clearTimeout(timer));
      this.#timers = [];
    }
  };
}
